import Hotjar from "@hotjar/browser";
import config from "config";
import { getRouteName } from "const/pages";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { IProfile } from "types";

const ENV_PROD = process.env.REACT_APP_ENV === "prod";

export function useChurnZero(user: Partial<IProfile>) {
  const location = useLocation();
  const [isChurnZeroLoaded, setChurnZeroLoaded] = useState(false);

  // Stores the last tracked path to prevent duplicate tracking
  const lastTrackedPathRef = useRef<string | null>(null);

  // Load ChurnZero script
  useEffect(() => {
    if (window.ChurnZero) {
      // console.log("ChurnZero is loaded.");
      setChurnZeroLoaded(true);
      return;
    }

    if (
      !config.CHURN_ZERO_URL_PROD ||
      !config.CHURN_ZERO_URL_DEV ||
      !config.CHURN_ZERO_API_KEY_PROD ||
      !config.CHURN_ZERO_API_KEY_DEV
    ) {
      console.error("ChurnZero: Missing URL or API Key.");
      return;
    }

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = ENV_PROD
      ? config.CHURN_ZERO_URL_PROD
      : config.CHURN_ZERO_URL_DEV;
    document.head.appendChild(script);

    const APP_KEY = ENV_PROD
      ? config.CHURN_ZERO_API_KEY_PROD
      : config.CHURN_ZERO_API_KEY_DEV;

    script.onload = () => {
      window.ChurnZero = window.ChurnZero || [];
      window.ChurnZero.push(["setAppKey", APP_KEY]);
      setChurnZeroLoaded(true);

      if (user?.id) {
        setUserInChurnZero(user);
      }
    };

    script.onerror = () => {
      console.error("ChurnZero: Failed to load the script.");
    };
  }, []);

  // Set user data
  useEffect(() => {
    if (!isChurnZeroLoaded || !user?.id) return;
    setUserInChurnZero(user);
  }, [user, isChurnZeroLoaded]);

  // Track unique page visits
  useEffect(() => {
    if (!isChurnZeroLoaded) return;

    const pathname = location.pathname;
    const pageName = getRouteName(pathname);
    if (!pageName) return;

    // Prevent sending the same page tracking event multiple times
    if (lastTrackedPathRef.current === pathname) return;
    lastTrackedPathRef.current = pathname;

    window.ChurnZero.push([
      "trackEvent",
      pageName,
      "Page Visited: " + pathname,
      null,
      { Vertical: config.VERTICAL },
    ]);

    // console.log(`ChurnZero: Tracked page visit - ${pageName}`);
  }, [location, isChurnZeroLoaded]);

  function setUserInChurnZero(user: Partial<IProfile>) {
    if (!window.ChurnZero) return;
    const userExternalAccountId =
      user?.externalAccountId || "no-external-user-id";
    const organizationExternalAccountId =
      user?.organization?.[0]?.externalAccountId ||
      user?.adminOf?.[0]?.externalAccountId ||
      user?.memberOf?.[0]?.externalAccountId ||
      "no-external-org-id";

    window.ChurnZero.push([
      "setContact",
      organizationExternalAccountId,
      userExternalAccountId,
    ]);
  }
}

export function useHotjarPageView() {
  const location = useLocation();
  useEffect(() => {
    Hotjar.stateChange(location.pathname);
  }, [location]);
}

export function useMenuState() {
  const [isOpenMenu, setIsOpenMenu] = useState(true);
  const [isHover, setIsHover] = useState(true);

  const toggleMenuHandler = useCallback(() => {
    setIsOpenMenu((state) => !state);
    setIsHover(false);
  }, [setIsOpenMenu]);
  const onMouseLeave = useCallback(() => {
    if (window.innerWidth > 830) {
      setIsHover(false);
    }
  }, [setIsHover]);
  const onMouseEnter = useCallback(() => {
    if (window.innerWidth > 830) {
      setIsHover(true);
    }
  }, [setIsHover]);

  return {
    toggleMenuHandler,
    onMouseEnter,
    onMouseLeave,
    isHover,
    showNavigation: isOpenMenu ? isOpenMenu : isHover,
  };
}
